import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { EventsService } from '../../@shared/services/events-service';
import { Horses } from '../../@shared/services/horses';
import { StaticService } from '../../@shared/services/static-service';
import { FamilyService } from '../../@shared/services/family-service';
import { MembershipService } from '../../@shared/services/membership-service';
import { StorageService } from '../../@core/services/storage.service';
import { ToastService } from '../../@core/services/toast.service';
import {
  EventClassResponse,
  EventContactResponse,
  EventResponse,
  EventServiceResponse,
  EventSponsorResponse,
  FamilyMemberResponse,
  HorseResponse,
  HorseRegistryResponse
} from '../../@shared/models/response.module';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { firstValueFrom } from 'rxjs';

interface ClassRegistration {
  event_class_id: number;
  class_name: string;
  user_id: string;
  user_name?: string;
  horse_id: number | null;
  horse_name?: string;
  price: number;
  price_currency: string;
  error?: string;
  expanded?: boolean;
}

interface ServiceRegistration {
  event_service_id: number;
  service_name: string;
  user_id: string;
  user_name?: string;
  num_registrations: number;
  price_per_unit: number;
  price_currency: string;
  expanded?: boolean;
}

@Component({
  selector: 'app-event-registration',
  standalone: false,
  templateUrl: './event-registration.html',
  styleUrls: ['./event-registration.css']
})
export class EventRegistrationComponent implements OnInit {
  eventId!: number;
  event: EventResponse | null = null;
  eventClasses: EventClassResponse[] = [];
  eventServices: EventServiceResponse[] = [];
  eventSponsors: EventSponsorResponse[] = [];
  eventContacts: EventContactResponse[] = [];
  allHorses: HorseResponse[] = [];
  familyMembers: {user_id: string, name: string}[] = [];

  currentUserId!: string;
  currentUserName!: string;

  classRegistrations: ClassRegistration[] = [];
  serviceRegistrations: ServiceRegistration[] = [];

  addHorseForm!: FormGroup;
  showAddHorseModal: boolean = false;
  showTermsModal: boolean = false;
  isLoadingHorses: boolean = false;
  isLoadingEvent: boolean = false;
  isLoadingClasses: boolean = false;
  isLoadingServices: boolean = false;
  isLoadingSponsors: boolean = false;
  isLoadingContacts: boolean = false;
  isSubmitting: boolean = false;

  horseRegistries: HorseRegistryResponse[] = [];
  horseGenders: {id: number, name: string}[] = [
    { id: 0, name: 'Mare' },
    { id: 1, name: 'Stallion' },
    { id: 2, name: 'Gelding' }
  ];

  constructor(
    private route: ActivatedRoute,
    public router: Router,
    private eventsService: EventsService,
    private horsesService: Horses,
    private staticService: StaticService,
    private familyService: FamilyService,
    private membershipService: MembershipService,
    private storageService: StorageService,
    private toastService: ToastService,
    private fb: FormBuilder
  ) {
    this.initAddHorseForm();
  }

  ngOnInit(): void {
    console.log('EventRegistrationComponent initialized');
    const user = this.storageService.getUser();
    console.log('Current user:', user);
    this.currentUserId = user.user_id;
    this.currentUserName = user.full_name || `${user.first_name} ${user.last_name}`;

    this.route.params.subscribe(params => {
      this.eventId = +params['eventId'];
      console.log('Event ID from route:', this.eventId);
      this.loadEventData();
      this.loadEventClasses();
      this.loadEventServices();
      this.loadEventSponsors();
      this.loadEventContacts();
      this.loadAllHorses();
      this.loadHorseRegistries();
      this.loadFamilyMembers();
    });
  }

  initAddHorseForm(): void {
    this.addHorseForm = this.fb.group({
      name: ['', Validators.required],
      date_of_birth: [''],
      passport_id: [''],
      horse_registry_id: [null],
      gender: [null, Validators.required],
      breed: [''],
      color: ['']
    });
  }

  loadEventData(): void {
    this.isLoadingEvent = true;
    this.eventsService.getEvents({ event_id: this.eventId }).subscribe({
      next: (response) => {
        if (response.data && response.data.length > 0) {
          this.event = response.data[0];
        }
        this.isLoadingEvent = false;
      },
      error: (error) => {
        console.error('Error loading event:', error);
        this.toastService.error('Failed to load event details');
        this.isLoadingEvent = false;
      }
    });
  }

  loadEventClasses(): void {
    this.isLoadingClasses = true;
    this.eventsService.getEventClasses({ event_id: this.eventId }).subscribe({
      next: (classes) => {
        console.log('Event classes loaded:', classes);
        this.eventClasses = classes;
        this.isLoadingClasses = false;
      },
      error: (error) => {
        console.error('Error loading event classes:', error);
        this.toastService.error('Failed to load event classes');
        this.isLoadingClasses = false;
      }
    });
  }

  loadEventServices(): void {
    this.isLoadingServices = true;
    this.eventsService.getEventServices({ event_id: this.eventId }).subscribe({
      next: (services) => {
        this.eventServices = services;
        this.isLoadingServices = false;
      },
      error: (error) => {
        console.error('Error loading event services:', error);
        this.toastService.error('Failed to load event services');
        this.isLoadingServices = false;
      }
    });
  }

  loadEventSponsors(): void {
    this.isLoadingSponsors = true;
    this.eventsService.getEventSponsors({ event_id: this.eventId }).subscribe({
      next: (sponsors) => {
        this.eventSponsors = sponsors;
        this.isLoadingSponsors = false;
      },
      error: (error) => {
        console.error('Error loading event sponsors:', error);
        this.isLoadingSponsors = false;
      }
    });
  }

  loadEventContacts(): void {
    this.isLoadingContacts = true;
    this.eventsService.getEventContacts({ event_id: this.eventId }).subscribe({
      next: (contacts) => {
        // Sort contacts by role_id with 0 first
        this.eventContacts = (contacts || []).sort((a, b) => {
          if (a.role_id === 0) return -1;
          if (b.role_id === 0) return 1;
          return a.role_id - b.role_id;
        });
        this.isLoadingContacts = false;
      },
      error: (error) => {
        console.error('Error loading event contacts:', error);
        this.isLoadingContacts = false;
      }
    });
  }

  loadAllHorses(): void {
    this.isLoadingHorses = true;
    this.horsesService.getHorses({}).subscribe({
      next: (horses: HorseResponse[]) => {
        this.allHorses = horses;
        this.isLoadingHorses = false;
      },
      error: (error: any) => {
        console.error('Error loading horses:', error);
        this.toastService.error('Failed to load horses');
        this.isLoadingHorses = false;
      }
    });
  }

  loadHorseRegistries(): void {
    this.staticService.getHorseRegistries().subscribe({
      next: (response: any) => {
        this.horseRegistries = response.data;
      },
      error: (error: any) => {
        console.error('Error loading horse registries:', error);
      }
    });
  }

  loadFamilyMembers(): void {
    // Start with the current user
    this.familyMembers = [{
      user_id: this.currentUserId,
      name: `${this.currentUserName} (Me)`
    }];

    // Load memberships to get family members
    this.membershipService.getUserMembership({}).subscribe({
      next: (memberships) => {
        memberships.forEach(membership => {
          if (membership.family && membership.family.members) {
            membership.family.members.forEach(member => {
              // Add family members that aren't the current user
              if (member.user.user_id !== this.currentUserId) {
                this.familyMembers.push({
                  user_id: member.user.user_id,
                  name: member.user.full_name || `${member.user.first_name} ${member.user.last_name}`
                });
              }
            });
          }
        });
      },
      error: (error) => {
        console.error('Error loading family members:', error);
      }
    });
  }

  addClassRegistration(eventClass: EventClassResponse): void {
    console.log('Adding class registration:', eventClass);
    // Collapse all existing registrations (both classes and services)
    this.classRegistrations.forEach(reg => reg.expanded = false);
    this.serviceRegistrations.forEach(reg => reg.expanded = false);

    const registration: ClassRegistration = {
      event_class_id: eventClass.event_class_id,
      class_name: eventClass.class_name,
      user_id: this.currentUserId,
      user_name: this.currentUserName,
      horse_id: null,
      price: eventClass.price || 0,
      price_currency: eventClass.price_currency || 'NOK',
      expanded: true
    };
    this.classRegistrations.push(registration);
    console.log('Class registrations now:', this.classRegistrations);
  }

  removeClassRegistration(index: number): void {
    this.classRegistrations.splice(index, 1);
  }

  onHorseSelected(registration: ClassRegistration, horseId: number | string): void {
    // Convert to number if it's a string
    const numericHorseId = typeof horseId === 'string' ? parseInt(horseId, 10) : horseId;

    if (!numericHorseId || isNaN(numericHorseId)) {
      registration.horse_id = null;
      registration.horse_name = undefined;
      registration.error = undefined;
      return;
    }

    const horse = this.allHorses.find(h => h.horse_id === numericHorseId);
    if (horse) {
      registration.horse_id = numericHorseId;
      registration.horse_name = horse.name;

      // Validate horse registry
      const eventClass = this.eventClasses.find(ec => ec.event_class_id === registration.event_class_id);
      if (eventClass && eventClass.req_horse_registration_id) {
        // If req_horse_registration_id is 3, allow all horses
        if (eventClass.req_horse_registration_id === 3) {
          registration.error = undefined;
        } else if (!horse.horse_registry_id || horse.horse_registry_id !== eventClass.req_horse_registration_id) {
          registration.error = 'This horse does not meet the registry requirements for this class';
        } else {
          registration.error = undefined;
        }
      } else {
        registration.error = undefined;
      }
    }
  }

  onUserSelected(registration: ClassRegistration, userId: string): void {
    const selectedMember = this.familyMembers.find(m => m.user_id === userId);
    if (selectedMember) {
      registration.user_name = selectedMember.name;
    }
  }

  addServiceRegistration(eventService: EventServiceResponse): void {
    const existingIndex = this.serviceRegistrations.findIndex(
      sr => sr.event_service_id === eventService.event_service_id && sr.user_id === this.currentUserId
    );

    if (existingIndex >= 0) {
      this.serviceRegistrations[existingIndex].num_registrations++;
      // Collapse all and expand only the existing one
      this.classRegistrations.forEach(reg => reg.expanded = false);
      this.serviceRegistrations.forEach((reg, idx) => reg.expanded = idx === existingIndex);
    } else {
      // Collapse all existing registrations (both classes and services)
      this.classRegistrations.forEach(reg => reg.expanded = false);
      this.serviceRegistrations.forEach(reg => reg.expanded = false);

      const registration: ServiceRegistration = {
        event_service_id: eventService.event_service_id,
        service_name: eventService.event_service_name,
        user_id: this.currentUserId,
        user_name: this.currentUserName,
        num_registrations: 1,
        price_per_unit: eventService.price || 0,
        price_currency: eventService.price_currency || 'NOK',
        expanded: true
      };
      this.serviceRegistrations.push(registration);
    }
  }

  updateServiceQuantity(registration: ServiceRegistration, quantity: number): void {
    if (quantity > 0) {
      registration.num_registrations = quantity;
    }
  }

  removeServiceRegistration(index: number): void {
    this.serviceRegistrations.splice(index, 1);
  }

  openAddHorseModal(): void {
    this.showAddHorseModal = true;
    this.addHorseForm.reset();
  }

  closeAddHorseModal(): void {
    this.showAddHorseModal = false;
  }

  openTermsModal(): void {
    this.showTermsModal = true;
  }

  closeTermsModal(): void {
    this.showTermsModal = false;
  }

  submitNewHorse(): void {
    if (this.addHorseForm.invalid) {
      this.toastService.error('Please fill in all required fields');
      return;
    }

    const horseData = {
      ...this.addHorseForm.value,
      user_id: this.currentUserId
    };

    this.horsesService.postHorse(horseData).subscribe({
      next: (newHorse: HorseResponse) => {
        this.toastService.success('Horse added successfully');
        this.allHorses.push(newHorse);
        this.closeAddHorseModal();
      },
      error: (error: any) => {
        console.error('Error adding horse:', error);
        this.toastService.error('Failed to add horse');
      }
    });
  }

  getTotalPrice(): number {
    let total = 0;

    // Add class registration prices
    this.classRegistrations.forEach(reg => {
      total += reg.price;
    });

    // Add service registration prices
    this.serviceRegistrations.forEach(reg => {
      total += reg.price_per_unit * reg.num_registrations;
    });

    return total;
  }

  getCurrency(): string {
    if (this.classRegistrations.length > 0) {
      return this.classRegistrations[0].price_currency;
    }
    if (this.serviceRegistrations.length > 0) {
      return this.serviceRegistrations[0].price_currency;
    }
    return 'NOK';
  }

  canSubmit(): boolean {
    if (this.isSubmitting) return false;

    // Check if there are any registrations
    if (this.classRegistrations.length === 0 && this.serviceRegistrations.length === 0) {
      return false;
    }

    // Check if all class registrations have horses selected
    for (const reg of this.classRegistrations) {
      if (!reg.horse_id || reg.error) {
        return false;
      }
    }

    return true;
  }

  async submitRegistrations(): Promise<void> {
    console.log('Submit button clicked!');
    console.log('Can submit?', this.canSubmit());
    console.log('Class registrations:', this.classRegistrations);
    console.log('Service registrations:', this.serviceRegistrations);

    if (!this.canSubmit()) {
      this.toastService.error('Please complete all registrations');
      return;
    }

    this.isSubmitting = true;

    try {
      // Submit class registrations
      for (const classReg of this.classRegistrations) {
        console.log('Submitting class registration:', {
          event_class_id: classReg.event_class_id,
          user_id: classReg.user_id,
          horse_id: classReg.horse_id
        });

        await firstValueFrom(this.eventsService.registerForEventClass({
          event_class_id: classReg.event_class_id,
          user_id: classReg.user_id,
          horse_id: classReg.horse_id!
        }));

        console.log('Class registration submitted successfully');
      }

      // Submit service registrations
      for (const serviceReg of this.serviceRegistrations) {
        console.log('Submitting service registration:', {
          event_id: this.eventId,
          event_service_id: serviceReg.event_service_id,
          user_id: serviceReg.user_id,
          num_registrations: serviceReg.num_registrations
        });

        await firstValueFrom(this.eventsService.registerForEventService({
          event_id: this.eventId,
          event_service_id: serviceReg.event_service_id,
          user_id: serviceReg.user_id,
          num_registrations: serviceReg.num_registrations
        }));

        console.log('Service registration submitted successfully');
      }

      console.log('All registrations completed successfully');
      this.toastService.success('Registration completed successfully!');
      this.router.navigate(['/event-details', this.eventId]);
    } catch (error: any) {
      console.error('Error submitting registrations:', error);
      console.error('Error details:', {
        message: error?.message,
        status: error?.status,
        error: error?.error
      });

      const errorMessage = error?.error?.message || error?.message || 'Failed to complete registration';
      this.toastService.error(errorMessage);
    } finally {
      this.isSubmitting = false;
    }
  }

  getAvailableUsers(): {user_id: string, name: string}[] {
    return [{ user_id: this.currentUserId, name: this.currentUserName }];
  }

  isClassFull(eventClass: EventClassResponse): boolean {
    if (!eventClass.max_num_registrations) return false;
    const currentRegistrations = eventClass.event_class_registrations?.length || 0;
    return currentRegistrations >= eventClass.max_num_registrations;
  }

  isServiceFull(eventService: EventServiceResponse): boolean {
    if (!eventService.max_num_registrations) return false;
    const currentRegistrations = eventService.event_service_registrations?.reduce(
      (sum, reg) => sum + (reg.num_registrations || 0), 0
    ) || 0;
    return currentRegistrations >= eventService.max_num_registrations;
  }

  getAvailableServiceSlots(eventService: EventServiceResponse): number {
    if (!eventService.max_num_registrations) return 999;
    const currentRegistrations = eventService.event_service_registrations?.reduce(
      (sum, reg) => sum + (reg.num_registrations || 0), 0
    ) || 0;
    return eventService.max_num_registrations - currentRegistrations;
  }

  getGoogleMapsLink(event: EventResponse): string {
    const address = `${event.address}, ${event.city}, ${event.country}`;
    const encodedAddress = encodeURIComponent(address);
    return `https://www.google.com/maps/search/?api=1&query=${encodedAddress}`;
  }

  shouldAnimateSponsors(): boolean {
    return this.eventSponsors.length > 3;
  }

  toggleClassRegistration(registration: ClassRegistration): void {
    const wasExpanded = registration.expanded;

    // Collapse all registrations (both classes and services)
    this.classRegistrations.forEach(reg => reg.expanded = false);
    this.serviceRegistrations.forEach(reg => reg.expanded = false);

    // Toggle this one (if it was collapsed, expand it; if it was expanded, keep it collapsed)
    registration.expanded = !wasExpanded;
  }

  toggleServiceRegistration(registration: ServiceRegistration): void {
    const wasExpanded = registration.expanded;

    // Collapse all registrations (both classes and services)
    this.classRegistrations.forEach(reg => reg.expanded = false);
    this.serviceRegistrations.forEach(reg => reg.expanded = false);

    // Toggle this one (if it was collapsed, expand it; if it was expanded, keep it collapsed)
    registration.expanded = !wasExpanded;
  }
}
